import FilterListIcon from '@mui/icons-material/FilterList';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import logo from '../../assets/images/042-market-research1.svg';
import { selectDoneAssessmentIdList } from '../../redux/reducers/assessmentReducer';
import { Criticality, selectFindingsList } from '../../redux/reducers/findingsReducer';
import { selectCustomFrameworks, selectStandardFrameworks } from '../../redux/reducers/frameworkReducer';
import ExpandableFinding from '../finding/ExpandableFinding';

const FilterTypes = Object.freeze({
  FRAMEWORK: 'frameworkId',
  CRITICALITY: 'criticality',
});

// TODO: Implement filter for control owner when possible
function FilterBar({ filterList, handleFilters }) {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState('');
  const [anchor, setAnchor] = useState(null);
  const standardFrameworks = useSelector(selectStandardFrameworks);
  const customFrameworks = useSelector(selectCustomFrameworks);
  const [selectedCriticalities, setSelectedCriticalities] = useState(Criticality.list());
  const [selectedFrameworks, setSelectedFrameworks] = useState([
    ...standardFrameworks.map((fw) => fw.id),
    ...customFrameworks.map((fw) => fw.id),
  ]);

  const handleChange = (event) => {
    const { name, value } = event;
    switch (name) {
      case FilterTypes.FRAMEWORK:
        if (selectedFrameworks.includes(value)) {
          setSelectedFrameworks(selectedFrameworks.filter((id) => id !== value));
        } else {
          setSelectedFrameworks([...selectedFrameworks, value]);
        }
        break;
      case FilterTypes.CRITICALITY:
        if (selectedCriticalities.includes(value)) {
          setSelectedCriticalities(selectedCriticalities.filter((crit) => crit !== value));
        } else {
          setSelectedCriticalities([...selectedCriticalities, value]);
        }
        break;
      default:
        break;
    }
  };

  const handleOpen = (event) => {
    setAnchor(event.currentTarget);
    setFilterOpen(event.target.name);
  };

  const handleClose = (name) => {
    switch (name) {
      case FilterTypes.FRAMEWORK:
        handleFilters({ name, value: selectedFrameworks });
        break;
      case FilterTypes.CRITICALITY:
        handleFilters({ name, value: selectedCriticalities });
        break;
      case 'clear':
        handleFilters({ name, value: [] });
        break;
      default:
        break;
    }
    setAnchor(null);
    setFilterOpen('');
  };

  const handleClearFilters = () => {
    setSelectedFrameworks([...standardFrameworks.map((fw) => fw.id), ...customFrameworks.map((fw) => fw.id)]);
    setSelectedCriticalities(Criticality.list());
    handleClose('clear');
  };

  useEffect(() => {
    handleClearFilters();
  }, [standardFrameworks, customFrameworks]);

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography>{t('management.filter.title')}</Typography>
      <Stack>
        <Button
          variant="outlined"
          color="secondary"
          name={FilterTypes.FRAMEWORK}
          onClick={handleOpen}
          id={FilterTypes.FRAMEWORK}
          aria-controls={filterOpen === FilterTypes.FRAMEWORK ? 'fwMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={filterOpen === FilterTypes.FRAMEWORK ? 'true' : undefined}
          startIcon={<FilterListIcon />}
        >
          {t('management.filter.framework')}
        </Button>
        <Menu
          id="fwMenu"
          anchorEl={anchor}
          name={FilterTypes.FRAMEWORK}
          open={filterOpen === FilterTypes.FRAMEWORK}
          onClose={() => handleClose(FilterTypes.FRAMEWORK)}
        >
          <ListSubheader>{t('framework.standard')}</ListSubheader>
          {standardFrameworks.map((framework) => (
            <MenuItem
              key={framework.id}
              name={FilterTypes.FRAMEWORK}
              value={framework.id}
              onClick={() => handleChange({ name: FilterTypes.FRAMEWORK, value: framework.id })}
            >
              <Checkbox variant="checkbox" checked={selectedFrameworks.includes(framework.id)} />
              <ListItemText>
                <Typography variant="bold">{framework.title}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
          {customFrameworks.length > 0 && <ListSubheader>{t('framework.custom')}</ListSubheader>}
          {customFrameworks.map((framework) => (
            <MenuItem
              key={framework.id}
              name={FilterTypes.FRAMEWORK}
              value={framework.id}
              onClick={() => handleChange({ name: FilterTypes.FRAMEWORK, value: framework.id })}
            >
              <Checkbox variant="checkbox" checked={selectedFrameworks.includes(framework.id)} />
              <ListItemText>
                <Typography variant="bold">{framework.title}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <Stack>
        <Button
          variant="outlined"
          color="secondary"
          name={FilterTypes.CRITICALITY}
          onClick={handleOpen}
          id={FilterTypes.CRITICALITY}
          aria-controls={filterOpen === FilterTypes.CRITICALITY ? 'criticalityMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={filterOpen === FilterTypes.CRITICALITY ? 'true' : undefined}
          startIcon={<FilterListIcon />}
        >
          {t('management.filter.criticality')}
        </Button>
        <Menu
          id="criticalityMenu"
          anchorEl={anchor}
          name={FilterTypes.CRITICALITY}
          open={filterOpen === FilterTypes.CRITICALITY}
          onClose={() => handleClose(FilterTypes.CRITICALITY)}
        >
          {Criticality.list().map((criticality) => (
            <MenuItem
              key={criticality}
              name={FilterTypes.CRITICALITY}
              value={criticality}
              onClick={() => handleChange({ name: FilterTypes.CRITICALITY, value: criticality })}
            >
              <Checkbox variant="checkbox" checked={selectedCriticalities.includes(criticality)} />
              <ListItemText>
                <Typography variant="bold">{t(`finding.criticality.${criticality}`)}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      {filterList.length > 0 && (
        <Button variant="text" onClick={() => handleClearFilters()} startIcon={<HighlightOffIcon />}>
          {t('management.filter.clear')}
        </Button>
      )}
    </Stack>
  );
}

function ManagementFindings() {
  const { t } = useTranslation();
  const findingsList = useSelector(selectFindingsList);
  const doneAssessmentIds = useSelector(selectDoneAssessmentIdList);
  const [resolved, setResolved] = useState([]);
  const [pending, setPending] = useState([]);
  const [resolvedFilters, setResolvedFilters] = useState([]);
  const [pendingFilters, setPendingFilters] = useState([]);
  const [resolvedFiltered, setResolvedFiltered] = useState([]);
  const [pendingFiltered, setPendingFiltered] = useState([]);

  useEffect(() => {
    const doneFindings = findingsList.filter((finding) => doneAssessmentIds.includes(finding.assessmentId));
    const { res, pen } = Object.groupBy(doneFindings, ({ resolved }) => (resolved ? 'res' : 'pen'));
    setResolved(res || []);
    setPending(pen || []);
  }, [doneAssessmentIds, findingsList]);

  useEffect(() => {
    const filtered = resolved.filter((finding) => (
      resolvedFilters.every((filter) => filter.value.includes(finding[filter.name]))
    ));
    setResolvedFiltered(filtered);
  }, [resolved, resolvedFilters]);

  useEffect(() => {
    const filtered = pending.filter((finding) => (
      pendingFilters.every((filter) => filter.value.includes(finding[filter.name]))
    ));
    setPendingFiltered(filtered);
  }, [pending, pendingFilters]);

  const handlePendingFilters = (filterObject) => {
    switch (filterObject.name) {
      case 'clear':
        setPendingFilters([]);
        break;
      default: {
        const index = pendingFilters.findIndex((entry) => entry.name === filterObject.name);
        if (index !== -1) {
          const newFilters = [...pendingFilters];
          newFilters[index] = filterObject;
          setPendingFilters(newFilters);
        } else {
          setPendingFilters([...pendingFilters, filterObject]);
        }
        break;
      }
    }
  };

  const handleResolvedFilters = (filterObject) => {
    switch (filterObject.name) {
      case 'clear':
        setResolvedFilters([]);
        break;
      default: {
        const index = resolvedFilters.findIndex((entry) => entry.name === filterObject.name);
        if (index !== -1) {
          const newFilters = [...resolvedFilters];
          newFilters[index] = filterObject;
          setResolvedFilters(newFilters);
        } else {
          setResolvedFilters([...resolvedFilters, filterObject]);
        }
        break;
      }
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflow: 'auto' }}
    >
      <Stack sx={{ maxWidth: '900px' }}>
        <Box my={4}>
          <Typography variant="tabHeader">{t('management.assessmentFindings.title')}</Typography>
        </Box>
        <Stack spacing={2} useFlexGap>
          <Stack>
            <Typography variant="regular">{t('management.assessmentFindings.description')}</Typography>
            <Typography variant="regular">{t('management.assessmentFindings.correctiveDescription')}</Typography>
          </Stack>
          <Typography variant="findingsSubheader">{t('management.assessmentFindings.pending')}</Typography>
          {pending.length > 0 ? (
            <>
              <Typography variant="regular">{t('management.assessmentFindings.pendingDescription')}</Typography>
              <FilterBar filterList={pendingFilters} handleFilters={handlePendingFilters} />
              {pendingFiltered
                .map((finding) => (
                  <ExpandableFinding key={finding.id} finding={finding} />
                ))}
            </>
          ) : (
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                {t('activityManagement.findings.pending')}
              </Typography>
              <Box sx={{ ml: '80px' }}>
                <img src={logo} width={'100px'} />
              </Box>
            </Stack>
          )}
        </Stack>
        <Divider sx={{ my: '32px' }} />
        <Stack spacing={2} useFlexGap sx={{ mb: 0.25 }}>
          <Typography variant="findingsSubheader">{t('management.assessmentFindings.resolved')}</Typography>
          {resolved.length > 0 ? (
            <>
              <FilterBar filterList={resolvedFilters} handleFilters={handleResolvedFilters} />
              {resolvedFiltered
                .map((finding) => (
                  <ExpandableFinding key={finding.id} finding={finding} />
                ))}
            </>
          ) : (
            <Stack spacing={3} useFlexGap>
              <Typography variant="subtitle1">
                {t('activityManagement.findings.resolved')}
              </Typography>
              <Box sx={{ ml: '80px' }}>
                <img src={logo} width={'100px'} />
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}

export default ManagementFindings;
